import lottie from 'lottie-web';
import Phaser from 'phaser';

import type { AnimationItem } from 'lottie-web';

type LottieCanvasSpriteConfig = {
	scene: Phaser.Scene;
	parent: Phaser.GameObjects.Container;
	sourceRoot: HTMLElement;
	textureKey: string;
	animationData: unknown;
	canvasWidth: number;
	canvasHeight: number;
	loop: boolean;
	originX?: number;
	originY?: number;
	onComplete?: () => void;
};

const LOTTIE_TEXTURE_SCALE = 2;

export class LottieCanvasSprite {
	private readonly animation: AnimationItem;
	private readonly sourceContainer: HTMLDivElement;
	private readonly handleDomLoaded: () => void;
	private readonly handleComplete: () => void;
	private texture: Phaser.Textures.CanvasTexture | null = null;
	private image: Phaser.GameObjects.Image | null = null;
	private displayWidth = 1;
	private displayHeight = 1;
	private visible = false;
	private playing = false;
	private flipX = false;
	private destroyed = false;

	constructor({
		scene,
		parent,
		sourceRoot,
		textureKey,
		animationData,
		canvasWidth,
		canvasHeight,
		loop,
		originX = 0.5,
		originY = 1,
		onComplete,
	}: LottieCanvasSpriteConfig) {
		this.sourceContainer = document.createElement('div');
		this.sourceContainer.style.width = `${canvasWidth}px`;
		this.sourceContainer.style.height = `${canvasHeight}px`;
		sourceRoot.appendChild(this.sourceContainer);

		this.animation = lottie.loadAnimation({
			container: this.sourceContainer,
			renderer: 'canvas',
			loop,
			autoplay: false,
			animationData,
			rendererSettings: {
				clearCanvas: true,
				dpr: LOTTIE_TEXTURE_SCALE,
				preserveAspectRatio: 'xMidYMid meet',
			},
		});

		this.handleDomLoaded = () => {
			if (this.destroyed || this.image) {
				return;
			}

			const canvas = this.sourceContainer.querySelector('canvas');
			// Lottie creates the element before its backing buffer is initialized.
			if (!(canvas instanceof HTMLCanvasElement) || canvas.width === 0 || canvas.height === 0) {
				return;
			}

			const texture = scene.textures.addCanvas(textureKey, canvas);
			if (!texture) {
				return;
			}

			this.texture = texture;
			this.image = scene.add
				.image(0, 0, textureKey)
				.setOrigin(originX, originY)
				.setDisplaySize(this.displayWidth, this.displayHeight)
				.setFlipX(this.flipX)
				.setVisible(this.visible);
			parent.add(this.image);
			this.refreshTexture();
		};
		this.handleComplete = () => {
			if (this.destroyed) {
				return;
			}

			this.playing = false;
			this.refreshTexture();
			onComplete?.();
		};

		this.handleDomLoaded();
		this.animation.addEventListener('DOMLoaded', this.handleDomLoaded);
		this.animation.addEventListener('complete', this.handleComplete);
	}

	update() {
		this.handleDomLoaded();
		if (!this.destroyed && this.visible && this.playing) {
			this.refreshTexture();
		}
	}

	setDisplaySize(width: number, height: number) {
		this.displayWidth = width;
		this.displayHeight = height;
		this.image?.setDisplaySize(width, height);
	}

	setVisible(visible: boolean) {
		this.visible = visible;
		this.image?.setVisible(visible);
		if (visible) {
			this.refreshTexture();
		}
	}

	setFlipX(flipX: boolean) {
		this.flipX = flipX;
		this.image?.setFlipX(flipX);
	}

	setSpeed(speed: number) {
		this.animation.setSpeed(speed);
	}

	play() {
		this.playing = true;
		this.animation.play();
	}

	playFromStart() {
		this.playing = true;
		this.animation.goToAndPlay(0, true);
	}

	pause() {
		this.playing = false;
		this.animation.pause();
	}

	stop() {
		this.playing = false;
		this.animation.stop();
		this.refreshTexture();
	}

	destroy() {
		if (this.destroyed) {
			return;
		}

		this.destroyed = true;
		this.playing = false;
		const image = this.image;
		const texture = this.texture;
		this.image = null;
		this.texture = null;
		this.animation.removeEventListener('DOMLoaded', this.handleDomLoaded);
		this.animation.removeEventListener('complete', this.handleComplete);
		this.animation.destroy();
		image?.destroy();
		if (this.isTextureLive(texture)) {
			texture.destroy();
		}
		this.sourceContainer.remove();
	}

	private refreshTexture() {
		const texture = this.texture;
		if (this.isTextureLive(texture)) {
			texture.refresh();
		}
	}

	private isTextureLive(texture: Phaser.Textures.CanvasTexture | null): texture is Phaser.Textures.CanvasTexture {
		return texture?.manager?.get(texture.key) === texture;
	}
}
